/**
 * CSV shaping for the Certifications Report (ADR-0003).
 *
 * The report has two views backed by two endpoints, but they are two
 * projections of ONE base row: an employee certification record joined to its
 * issuer, category and catalog entry. The By-Issuer tree is a grouping of that
 * grain, not extra data.
 *
 * So there is ONE canonical export grain — one row per (individual ×
 * certification record) — and both views flatten into it. The invariant the
 * test suite asserts, and the reason this file exists rather than two
 * view-specific exporters:
 *
 *     same filters => byte-identical CSV, whichever view you exported from.
 */

import { getExpirationLabel } from '../constants/certificationFilters';

/** Column order of the canonical grain. Meaningful columns first, join keys last. */
export const CERTIFICATION_CSV_FIELDS = [
  'fullName',
  'email',
  'certificationName',
  'issuer',
  'issuerType',
  'category',
  'catalogEntry',
  'issuedDate',
  'expirationDate',
  'status',
  'website',
  'employeeId',
  'certificationId'
];

/**
 * Human-readable headers. This file is read in Excel, not re-imported, so it
 * uses labels rather than the machine field names the DataImport round trip needs.
 *
 * "Individual" matches the report's own UI vocabulary (toggle, stats, empty
 * states) rather than the repo-wide "Consultant" preference: a file whose
 * wording disagrees with the page that produced it is worse than one that is
 * inconsistent with a different page.
 */
export const CERTIFICATION_CSV_HEADERS = {
  fullName: 'Full Name',
  email: 'Email',
  certificationName: 'Certification Name',
  issuer: 'Issuer',
  issuerType: 'Issuer Type',
  category: 'Category',
  catalogEntry: 'Catalog Entry',
  issuedDate: 'Issued Date',
  expirationDate: 'Expiration Date',
  status: 'Status',
  website: 'Website',
  employeeId: 'Employee ID',
  certificationId: 'Certification ID'
};

export const ISSUER_TYPE_LABELS = {
  CUSTOM: 'Custom',
  STANDARD: 'Standard'
};

/**
 * `yyyy-MM-dd` from an API date value.
 *
 * Issued and expiration dates are date-only columns (`@db.Date`) and arrive as
 * midnight-UTC ISO strings, so the string is SLICED rather than re-parsed:
 * `new Date(value)` shifts the day in any browser at a negative UTC offset, and
 * `toLocaleDateString()` hands Excel a locale-ambiguous `3/1/2024` that it then
 * re-reads by its own locale. See ADR-0003 §2.3.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string} `yyyy-MM-dd`, or '' when absent
 */
export const toIsoDate = (value) => {
  if (!value) {
    return '';
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  return String(value).slice(0, 10);
};

/**
 * 'Custom' | 'Standard' | '' — blank when the issuer (and so its type) is unknown.
 * @param {boolean|null|undefined} isCustom
 * @returns {string}
 */
export const toIssuerTypeLabel = (isCustom) => {
  if (isCustom === undefined || isCustom === null) {
    return '';
  }

  return isCustom ? ISSUER_TYPE_LABELS.CUSTOM : ISSUER_TYPE_LABELS.STANDARD;
};

/**
 * Flatten the By-Individual payload (`GET /api/reports/certifications`).
 *
 * @param {Array} data - response `data`
 * @returns {Array<Object>} canonical-grain rows
 */
export const flattenByIndividual = (data = []) =>
  data.flatMap((employee) =>
    (employee?.certifications || []).map((cert) => ({
      fullName: employee.fullName || '',
      email: employee.email || '',
      certificationName: cert.name || '',
      issuer: cert.issuer?.name || '',
      issuerType: toIssuerTypeLabel(cert.issuer?.isCustom),
      category: cert.category?.name || '',
      catalogEntry: cert.catalog?.name || '',
      issuedDate: toIsoDate(cert.issuedDate),
      expirationDate: toIsoDate(cert.expirationDate),
      status: getExpirationLabel(cert.expirationStatus),
      website: cert.website || '',
      employeeId: employee.employeeId || '',
      certificationId: cert.id || ''
    }))
  );

/**
 * Flatten the By-Issuer payload (`GET /api/reports/certifications/by-issuer`).
 *
 * Produces the same grain and the same columns as flattenByIndividual — the
 * tree's three levels collapse back into the row they were grouped from.
 *
 * IMPORTANT: reads each employee entry's OWN certification fields, not the
 * enclosing tree node's. The node is presentation — it merges issuer and
 * certification names case-insensitively and labels itself with whichever record
 * arrived first, and it substitutes a placeholder for a missing issuer. Reading
 * the node would make this export disagree with the by-individual one on casing
 * variants and on certifications with no issuer. The node values remain as a
 * fallback for older payloads.
 *
 * @param {Array} data - response `data`
 * @returns {Array<Object>} canonical-grain rows
 */
export const flattenByIssuer = (data = []) =>
  data.flatMap((issuer) =>
    (issuer?.certifications || []).flatMap((cert) =>
      (cert?.employees || []).map((employee) => {
        const recordIssuer = employee.issuer !== undefined ? employee.issuer : issuer;
        const recordCategory = employee.category !== undefined ? employee.category : cert.category;
        const recordCatalog = employee.catalog !== undefined ? employee.catalog : cert.catalog;

        return {
          fullName: employee.fullName || '',
          email: employee.email || '',
          certificationName: employee.certificationName || cert.name || '',
          issuer: recordIssuer?.name || '',
          issuerType: toIssuerTypeLabel(recordIssuer?.isCustom),
          category: recordCategory?.name || '',
          catalogEntry: recordCatalog?.name || '',
          issuedDate: toIsoDate(employee.issuedDate),
          expirationDate: toIsoDate(employee.expirationDate),
          status: getExpirationLabel(employee.expirationStatus),
          website: employee.website || '',
          employeeId: employee.employeeId || '',
          certificationId: employee.certificationId || ''
        };
      })
    )
  );

/**
 * Locale PINNED to 'en', at DEFAULT (variant) sensitivity.
 *
 * Pinned because the byte-identical invariant must not depend on the reader's
 * browser locale. The visible cost is accepted: under `en`, `Żaneta Król` sorts
 * before `Zbigniew Wójcik`, whereas Polish collation treats `Ż` as a distinct
 * letter after `Z`. No single locale is right for a roster mixing Polish,
 * Romanian and other names, and Excel re-sorts by the reader's locale in a click.
 *
 * NOT `{ sensitivity: 'base' }`: base folds diacritics onto base letters
 * (`Ł`->`L`, `ś`->`s`, `ș`->`s`, `ă`->`a`), so `Łukasz Wiśniewski` and
 * `Lukasz Wisniewski` — two different people — would compare EQUAL on the name
 * key. Base sensitivity is a search-matching setting; this is a sort key.
 */
const compareText = (a, b) => (a || '').localeCompare(b || '', 'en');

/**
 * Deterministic row order, so the same filters produce the same bytes from
 * either view.
 *
 * @param {Array<Object>} rows
 * @returns {Array<Object>} a new sorted array
 */
export const sortCertificationRows = (rows = []) =>
  [...rows].sort(
    (a, b) =>
      compareText(a.fullName, b.fullName) ||
      compareText(a.issuer, b.issuer) ||
      compareText(a.certificationName, b.certificationName) ||
      compareText(a.issuedDate, b.issuedDate) ||
      compareText(a.certificationId, b.certificationId)
  );

/**
 * Canonical-grain rows for whichever view is active.
 *
 * @param {'employee'|'issuer'} viewMode
 * @param {Object} payloads
 * @param {Array} payloads.employeeData - By-Individual response data
 * @param {Array} payloads.issuerData - By-Issuer response data
 * @returns {Array<Object>} sorted rows
 */
export const buildCertificationCsvRows = (viewMode, { employeeData = [], issuerData = [] } = {}) =>
  sortCertificationRows(
    viewMode === 'issuer' ? flattenByIssuer(issuerData) : flattenByIndividual(employeeData)
  );

/**
 * `certifications_report_yyyy-MM-dd.csv`, matching the underscore convention of
 * the existing export helpers in csvExport.js.
 *
 * No applied-filter suffix and no metadata preamble: every filter dimension is
 * already a column, so the filter set is recoverable from the file's contents.
 *
 * @param {Date} [now=new Date()]
 * @returns {string}
 */
export const buildCertificationsCsvFilename = (now = new Date()) =>
  `certifications_report_${now.toISOString().split('T')[0]}.csv`;
